import React, { useEffect, useState } from 'react';
import api from "../../services/api.js";
import { useNavigate } from 'react-router-dom';
import logobody from '../../images/nortesoft_extendida_2_semfundo.png';
import {
    clearSessionData,
    notifySessionTokenUpdated,
} from "../../services/sessionManager.js";

import './login.css';

const emptyCompany = {
    cdemp: '',
    deemp: '',
    ativoemp: '',
    apelido: '',
    Cidemp: '',
    Estemp: '',
    Baiemp: '',
    TipoEmp: '',
};

const emptyChallenge = null;

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [empresas, setEmpresas] = useState([]);
    const navigate = useNavigate();
    const [buttonText, setButtonText] = useState('Login');
    const [empresaescohida, setEmpresaEscolhida] = useState(emptyCompany);
    const [twoFactorChallenge, setTwoFactorChallenge] = useState(emptyChallenge);
    const [twoFactorCode, setTwoFactorCode] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [infoMessage, setInfoMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isResendingCode, setIsResendingCode] = useState(false);

    const resetSecondFactorState = () => {
        setTwoFactorChallenge(emptyChallenge);
        setTwoFactorCode('');
        setInfoMessage('');
    };

    const resetLoginFlow = () => {
        clearSessionData();
        notifySessionTokenUpdated();
        setEmpresas([]);
        setEmpresaEscolhida(emptyCompany);
        setButtonText('Login');
        setErrorMessage('');
        resetSecondFactorState();
        setIsLoading(false);
        setIsResendingCode(false);
    };

    const carregarEmpresas = async () => {
        const resp = await api.get(`/t_emp/user/${email}`, {
            params: {
                user: email,
            }
        });

        const modifiedData = {
            total_registros: resp.data.count,
            dados: resp.data.rows
        };

        if (modifiedData.total_registros > 0) {
            setEmpresas(modifiedData.dados);
            setEmpresaEscolhida(modifiedData.dados[0]);
            setButtonText('Acessar');
            return;
        }

        setEmpresas([]);
        alert("Nenhuma unidade encontrada para este usuario.");
    };

    const concluirAutenticacao = async (authData) => {
        const jwtToken = authData?.token;

        if (!jwtToken) {
            throw new Error("Token nao recebido.");
        }

        localStorage.setItem('token', jwtToken);
        localStorage.setItem('user', email.trim());
        notifySessionTokenUpdated();

        await carregarEmpresas();
    };

    const autenticarUsuario = async () => {
        const loginResponse = await api.post(`/auth/login`, {
            login: email,
            senha: password
        });

        if (loginResponse.data?.status === 'two_factor_required') {
            setTwoFactorChallenge({
                challengeId: loginResponse.data.challengeId,
                twoFactorMethod: loginResponse.data.twoFactorMethod,
                maskedEmail: loginResponse.data.maskedEmail || '',
            });
            setTwoFactorCode('');
            setInfoMessage(loginResponse.data.message || '');
            return null;
        }

        return loginResponse.data;
    };

    const confirmarSegundoFator = async () => {
        if (!twoFactorChallenge?.challengeId) {
            throw new Error('Desafio de autenticacao nao encontrado.');
        }

        const normalizedCode = twoFactorCode.trim();
        if (!normalizedCode) {
            throw new Error('Codigo de autenticacao nao informado.');
        }

        const confirmResponse = await api.post(`/auth/login/2fa`, {
            challengeId: twoFactorChallenge.challengeId,
            code: normalizedCode,
        });

        return confirmResponse.data;
    };

    const reenviarCodigo = async () => {
        if (!twoFactorChallenge?.challengeId) {
            return;
        }

        setErrorMessage('');
        setInfoMessage('');
        setIsResendingCode(true);

        try {
            const response = await api.post(`/auth/login/2fa/resend`, {
                challengeId: twoFactorChallenge.challengeId,
            });

            setTwoFactorChallenge((current) => current ? {
                ...current,
                challengeId: response.data.challengeId,
                maskedEmail: response.data.maskedEmail || current.maskedEmail,
            } : current);
            setInfoMessage(response.data.message || 'Codigo reenviado com sucesso.');
        } catch (err) {
            const message = err?.response?.data?.message || err?.message || "Falha ao reenviar o codigo.";
            setErrorMessage(Array.isArray(message) ? message.join('\n') : message);
        } finally {
            setIsResendingCode(false);
        }
    };

    const acessarEmpresaSelecionada = async () => {
        if (!empresaescohida.cdemp) {
            alert("Selecione uma unidade.");
            return;
        }

        const resp = await api.get(`/t_emp/${empresaescohida.cdemp}`, {
            params: {
                user: email,
            }
        });

        const empresaCompleta = resp.data || {};

        localStorage.setItem("cdemp", String(empresaCompleta.cdemp || empresaescohida.cdemp));
        localStorage.setItem("cidemp", (empresaCompleta.Cidemp || empresaCompleta.cidemp || '').trim());
        localStorage.setItem("estemp", (empresaCompleta.Estemp || empresaCompleta.estemp || '').trim());
        localStorage.setItem("baiemp", (empresaCompleta.Baiemp || empresaCompleta.baiemp || '').trim());
        localStorage.setItem("tipoemp", (empresaCompleta.TipoEmp || empresaCompleta.tipoemp || '').trim());
        localStorage.setItem("user", email.trim());

        navigate("/dashboard");
    };

    const handleLogin = async () => {
        try {
            setErrorMessage('');
            if (buttonText === 'Login') {
                setIsLoading(true);
                if (twoFactorChallenge) {
                    const authData = await confirmarSegundoFator();
                    await concluirAutenticacao(authData);
                } else {
                    const authData = await autenticarUsuario();
                    if (authData) {
                        await concluirAutenticacao(authData);
                    }
                }
                return;
            }

            setIsLoading(true);
            await acessarEmpresaSelecionada();
        } catch (err) {
            const message = err?.response?.data?.message || err?.message || "Falha ao autenticar.";
            setErrorMessage(Array.isArray(message) ? message.join('\n') : message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        setEmail('');
        setPassword('');
        resetLoginFlow();
        navigate("/");
    };

    const handleCancelTwoFactor = () => {
        setErrorMessage('');
        resetSecondFactorState();
    };

    const handleSelectEmpChange = (e) => {
        const selectedCdemp = Number(e.target.value);
        const selectedEmpresa = empresas.find(emp => emp.cdemp === selectedCdemp);
        setEmpresaEscolhida(selectedEmpresa || emptyCompany);
    };

    useEffect(() => {
        localStorage.setItem("user", email.trim());
    }, [email]);

    return (
        <div className='login-page'>
            <div className="login-container">
                <div className='login-brand'>
                    <img src={logobody} alt="logobody" className="logobody" />
                </div>

                <div className="login-form">
                    <input
                        type="email"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={buttonText !== 'Login' || Boolean(twoFactorChallenge)}
                    />
                    <input
                        type="password"
                        placeholder="Senha"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={buttonText !== 'Login' || Boolean(twoFactorChallenge)}
                    />

                    {twoFactorChallenge ? (
                        <div className="two-factor-panel">
                            <small className="login-label">
                                {twoFactorChallenge.twoFactorMethod === 'authenticator'
                                    ? 'Informe o codigo atual do Google Authenticator.'
                                    : `Informe o codigo enviado para ${twoFactorChallenge.maskedEmail || 'o seu email'}.`}
                            </small>
                            <input
                                type="text"
                                placeholder="Codigo de autenticacao"
                                value={twoFactorCode}
                                onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                maxLength={6}
                                disabled={isLoading}
                            />
                            {infoMessage ? (
                                <div className="login-message info">{infoMessage}</div>
                            ) : null}
                            <div className="two-factor-actions">
                                <button
                                    className="btn btn-outline-secondary"
                                    type="button"
                                    onClick={handleCancelTwoFactor}
                                    disabled={isLoading || isResendingCode}
                                >
                                    Cancelar 2FA
                                </button>
                                {twoFactorChallenge.twoFactorMethod === 'email' ? (
                                    <button
                                        className="btn btn-outline-primary"
                                        type="button"
                                        onClick={reenviarCodigo}
                                        disabled={isLoading || isResendingCode}
                                    >
                                        {isResendingCode ? 'Reenviando...' : 'Reenviar codigo'}
                                    </button>
                                ) : null}
                            </div>
                        </div>
                    ) : null}

                    {errorMessage ? (
                        <div className="login-message error">{errorMessage}</div>
                    ) : null}

                    <div className='col-xl-12 col-md-12 col-sm-12 mt-0'>
                        <small className="login-label">Unidade</small>
                        <select
                            name="unidade"
                            className="form-control mt-1 mb-3"
                            value={empresaescohida.cdemp}
                            onChange={handleSelectEmpChange}
                            disabled={buttonText !== 'Acessar' || isLoading}
                        >
                            {empresas.map((item) => (
                                <option key={item.cdemp} value={item.cdemp}>
                                    {item.apelido || item.fantemp || item.deemp}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="buttons">
                        <button className="btn btn-secondary" onClick={handleCancel}>Voltar</button>
                        <button className="btn btn-primary" onClick={handleLogin} disabled={isLoading}>
                            {isLoading
                                ? (twoFactorChallenge ? 'Confirmando...' : 'Processando...')
                                : (twoFactorChallenge ? 'Confirmar codigo' : buttonText)}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
